import * as React from "react";
import { Trans, useTranslation } from "react-i18next";
import { cn } from "@/lib/utils";
import { ArrowUpRight, ChevronRight } from "lucide-react";
import { StatsStrip } from "./StatsStrip";
import { SectionClouds } from "./SectionClouds";

interface Row {
  verb: string;
  time: string;
  titleLead: string;
  titleEm: string;
  desc: string;
}

function ClusterHead({ name, tag, dark }: { name: string; tag: string; dark?: boolean }) {
  return (
    <div className="mb-4 flex items-baseline gap-3">
      <span
        className={cn(
          "text-[18px] font-medium tracking-[-0.02em]",
          dark ? "text-page-bg" : "text-ink",
        )}
      >
        {name}
      </span>
      <span
        className={cn(
          "mono text-[11px] tracking-[0.14em] uppercase",
          dark ? "text-page-bg/55" : "text-ink-3",
        )}
      >
        {tag}
      </span>
      <span className={cn("h-px flex-1", dark ? "bg-page-bg/15" : "bg-line")} />
    </div>
  );
}

function ServiceRow({ row, isLast, dark }: { row: Row; isLast?: boolean; dark?: boolean }) {
  return (
    <div
      className={cn(
        "group flex flex-col gap-3 py-5 transition-colors sm:grid sm:grid-cols-[110px_1fr_auto] sm:items-start sm:gap-7 sm:py-6",
        !isLast && (dark ? "border-b border-page-bg/10" : "border-b border-line"),
      )}
    >
      <div className="flex items-baseline gap-3 sm:block">
        <div
          className={cn(
            "text-[14px] font-medium tracking-tight",
            dark ? "text-page-bg" : "text-ink",
          )}
        >
          {row.verb}
        </div>
        <div
          className={cn(
            "mono text-[11px] tracking-tight sm:mt-1",
            dark ? "text-page-bg/55" : "text-ink-3",
          )}
        >
          {row.time}
        </div>
      </div>

      <div className="min-w-0">
        <h3
          className={cn(
            "text-[clamp(17px,1.6vw,20px)] leading-snug font-medium tracking-[-0.02em]",
            dark ? "text-page-bg" : "text-ink",
          )}
        >
          {row.titleLead}{" "}
          <span className={cn("serif-italic", !dark && "text-accent")}>{row.titleEm}</span>
        </h3>
        <p
          className={cn(
            "mt-2 max-w-[58ch] text-[13.5px] leading-[1.55]",
            dark ? "text-page-bg/70" : "text-ink-2",
          )}
        >
          {row.desc}
        </p>
      </div>

      <ArrowUpRight
        className={cn(
          "mt-1 hidden h-4 w-4 transition-colors sm:block",
          dark ? "text-page-bg/40 group-hover:text-accent" : "text-ink-3 group-hover:text-accent",
        )}
        strokeWidth={2}
        aria-hidden="true"
      />
    </div>
  );
}

export function Services() {
  const { t } = useTranslation();
  const [isVisible, setIsVisible] = React.useState(false);
  const ref = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    const obs = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) {
          setIsVisible(true);
          obs.disconnect();
        }
      },
      { threshold: 0.1 },
    );
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, []);

  const core = t("landing.services.core.rows", { returnObjects: true }) as Row[];
  const audit = t("landing.services.audit.rows", { returnObjects: true }) as Row[];
  const premium = t("landing.services.premium.row", { returnObjects: true }) as Row;

  return (
    <section ref={ref} id="services" className="frame bg-surface-2/40">
      <SectionClouds variant="services" />
      <div className="container-narrow section-pad relative z-10">
        <div
          className={cn(
            "transition-all duration-700",
            isVisible ? "translate-y-0 opacity-100" : "translate-y-6 opacity-0",
          )}
        >
          <span className="eyebrow">
            <span className="num">02 —</span> {t("landing.services.eyebrow")}
          </span>
          <h2 className="section-title">
            <Trans
              i18nKey="landing.services.title"
              components={{ italic: <span className="serif-italic" /> }}
            />
          </h2>
          <p className="section-lede">{t("landing.services.lede")}</p>
        </div>

        <div
          className={cn(
            "mt-14 grid grid-cols-1 items-stretch gap-8 lg:grid-cols-2 lg:gap-12",
            "transition-all duration-700",
            isVisible ? "translate-y-0 opacity-100" : "translate-y-6 opacity-0",
          )}
          style={{ transitionDelay: isVisible ? "100ms" : "0ms" }}
        >
          <div className="flex flex-col">
            <ClusterHead
              name={t("landing.services.core.name")}
              tag={t("landing.services.core.tag")}
            />
            <div className="flex-1 rounded-[18px] border border-line bg-surface px-5 sm:px-7 lg:grid lg:grid-rows-3">
              {core.map((row, i) => (
                <ServiceRow key={`core-${i}`} row={row} isLast={i === core.length - 1} />
              ))}
            </div>
          </div>

          <div className="flex flex-col">
            <ClusterHead
              name={t("landing.services.audit.name")}
              tag={t("landing.services.audit.tag")}
            />
            <div className="flex-1 rounded-[18px] border border-line bg-surface px-5 sm:px-7 lg:grid lg:grid-rows-3">
              {audit.map((row, i) => (
                <ServiceRow key={`audit-${i}`} row={row} isLast={i === audit.length - 1} />
              ))}
            </div>
          </div>
        </div>

        <div
          className={cn(
            "mt-10",
            "transition-all duration-700",
            isVisible ? "translate-y-0 opacity-100" : "translate-y-6 opacity-0",
          )}
          style={{ transitionDelay: isVisible ? "200ms" : "0ms" }}
        >
          <ClusterHead
            name={t("landing.services.premium.name")}
            tag={t("landing.services.premium.tag")}
          />
          <div className="relative overflow-hidden rounded-[18px] bg-ink px-5 text-page-bg sm:px-7">
            <div
              aria-hidden="true"
              className="pointer-events-none absolute -top-20 -right-20 h-72 w-72 rounded-full"
              style={{
                background: "radial-gradient(circle, rgba(239,77,35,0.22), transparent 70%)",
              }}
            />
            <div className="relative flex flex-col gap-3 py-5 sm:grid sm:grid-cols-[110px_1fr_auto] sm:items-start sm:gap-7 sm:py-6">
              <div className="flex items-baseline gap-3 sm:block">
                <div className="text-[14px] font-medium tracking-tight text-page-bg">
                  {premium.verb}
                </div>
                <div className="mono text-[11px] tracking-tight text-page-bg/55 sm:mt-1">
                  {premium.time}
                </div>
              </div>
              <div className="min-w-0">
                <h3 className="text-[clamp(17px,1.6vw,20px)] leading-snug font-medium tracking-[-0.02em] text-page-bg">
                  {premium.titleLead} <span className="serif-italic">{premium.titleEm}</span>
                </h3>
                <p className="mt-2 max-w-[68ch] text-[13.5px] leading-[1.55] text-page-bg/70">
                  {premium.desc}
                </p>
              </div>
              <button
                onClick={() => {
                  const el = document.querySelector("#pricing");
                  if (el) el.scrollIntoView({ behavior: "smooth" });
                }}
                className="mt-1 inline-flex items-center gap-1.5 self-start rounded-full bg-accent py-1.5 pr-1.5 pl-4 text-[13px] font-medium tracking-tight whitespace-nowrap text-white transition-colors hover:bg-accent-hover sm:mt-0 sm:self-center"
              >
                {t("landing.services.premium.cta")}
                <span className="inline-flex h-5 w-5 items-center justify-center rounded-full bg-white/20">
                  <ChevronRight className="h-3 w-3" strokeWidth={2.5} />
                </span>
              </button>
            </div>
          </div>
        </div>

        <StatsStrip />
      </div>
    </section>
  );
}
